import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactCountryFlag from "react-country-flag";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  FiltersBar,
  Icon,
  Money,
  SearchInput,
  Segmented,
  Select,
  SortSelect,
  StatePanel,
  Toggle,
} from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import ArbitrageGlobeModal from "../components/ArbitrageGlobeModal.jsx";
import MarketSignalsPanel from "../components/MarketSignalsPanel.jsx";
import { SequenceBuilder, SequenceCommand, SequenceMatch, SequenceTabLabel } from "../components/ArbitrageSequences.jsx";
import { fetchSequenceResults, fetchSequences } from "../components/arbitrageSequencesApi.js";
import { regionFlag, regionLabel, routesFromLegs } from "../components/arbitrageRegions.js";
import { loadWorld } from "../../tradeFeed/globeGeometry.js";
import "./core-list-arbitrages.scss";

/**
 * Geographic arbitrage across the whole catalogue (Alex, 2026-10-12).
 *
 * Every route the backend precomputes nightly (`arbitrage:compute`): any two
 * legs of a reference — a leg is a (pool, region), pool being trade groups or
 * market (Chrono24, WatchFinder, auction houses) — whose medians differ by 5%
 * or more. Trade→Trade, Market→Market and Trade↔Market, every pair rather than
 * only the widest, paged rather than cut at 25.
 *
 * PRICE GAP and SUPPLY PRESSURE are the same routes ranked two ways: by gap,
 * or by how the quotes sit (plentiful on the cheap leg, scarce on the dear
 * one). Demand is still INFERRED from supply — no quote carries a buyer's
 * region — and the page says so.
 *
 * Filters, sort and page live in the URL so a filtered view can be shared.
 */

/**
 * "All" is every route ranked by Opportunity score (Alex, 2026-10-19: a tab
 * called All next to Price gap and Supply pressure). Saved sequences follow
 * as their own tabs, `seq-<id>`.
 */
const VIEWS = [
  { value: "opportunity", label: "All" },
  { value: "profit_week", label: "Profit per week" },
  { value: "price_gaps", label: "Price gap" },
  { value: "supply_pressure", label: "Supply pressure" },
];

/** One line per view; the detail opens on tap. */
const EXPLAIN = {
  opportunity: {
    line: "Profit, return, demand, supply and confidence, 20 points each.",
    detail: "Profit is the expected £ after the usual gap between asking and selling price (market −8%, trade −3%). Return is that profit as % of the buy price. Demand counts WTB leads in the last 3 months, double when the budget covers the buy price. Supply is the buy side's share of data points. Confidence comes from how many data points back the thinner side and how fresh they are. Each part is ranked against every profitable route on tonight's board.",
  },
  profit_week: {
    line: "Expected profit for each week a listing usually takes to sell.",
    detail: "Expected profit divided by how long a market listing usually stays up in the sell region. Routes selling into trade have no listing lifetime and rank last.",
  },
  price_gaps: {
    line: "Median price gaps of 5% or more between markets.",
    detail: "Every pair of regions and sources where the same reference's median price differs by 5% or more. Trade = WhatsApp trade groups; Market = Chrono24, WatchFinder and auction houses.",
  },
  supply_pressure: {
    line: "Plentiful and cheap where you buy, scarce and dear where you sell.",
    detail: "The same routes, ranked by how supply sits. Demand is inferred from supply here, since no quote carries a buyer's region.",
  },
};

/** The sort each view ranks by. */
const VIEW_SORT = { opportunity: "opp_score", profit_week: "profit_week", price_gaps: "gap_percent", supply_pressure: "score" };

const WINDOWS = [
  { value: "1", label: "1M" },
  { value: "3", label: "3M" },
  { value: "6", label: "6M" },
  { value: "12", label: "12M" },
];

/** Trade ↔ Market is the default tab (Alex, 2026-10-13); "all" is explicit. */
const ROUTE_TYPES = [
  { value: "all", label: "All routes" },
  { value: "trade", label: "Trade → Trade" },
  { value: "market", label: "Market → Market" },
  { value: "cross", label: "Trade ↔ Market" },
];

const MIN_GAPS = [
  { value: "10", label: "10%+" },
  { value: "20", label: "20%+" },
  { value: "30", label: "30%+" },
  { value: "50", label: "50%+" },
];

const SORTS = [
  { field: "opp_score", direction: "desc", label: "Opportunity score — highest first" },
  { field: "profit", direction: "desc", label: "Expected profit — highest first" },
  { field: "profit_week", direction: "desc", label: "Profit per week — highest first" },
  { field: "gap_percent", direction: "desc", label: "Gap % — highest first" },
  { field: "gap_percent", direction: "asc", label: "Gap % — lowest first" },
  { field: "gap", direction: "desc", label: "Gap in money — highest first" },
  { field: "avg_price", direction: "desc", label: "Average price — highest first" },
  { field: "avg_price", direction: "asc", label: "Average price — lowest first" },
  { field: "depth", direction: "desc", label: "Most data points first" },
  { field: "last_quote_at", direction: "desc", label: "Freshest first" },
  { field: "score", direction: "desc", label: "Supply pressure — strongest first" },
];

const POOL_LABEL = { trade: "Trade", market: "Market" };

const PER_PAGE = 24;

/** Multi-select region filters travel as "asia,uk" in the URL and the API. */
const MULTI = ["buy_region", "sell_region"];

/** Kept out of the region dropdowns (Alex, 2026-10-12): too few quotes to trade on. */
const HIDDEN_REGIONS = ["south-america"];

const splitList = (raw) => (raw ? raw.split(",").filter(Boolean) : []);

/** Read the page state from the URL, with the defaults filled in. */
const readParams = (search) => {
  const p = new URLSearchParams(search);
  const raw = p.get("view") || "";
  const view = VIEWS.some((v) => v.value === raw) || /^seq-\d+$/.test(raw) ? raw : "opportunity";
  return {
    view,
    months: ["1", "3", "6", "12"].includes(p.get("months")) ? p.get("months") : "12",
    route_type: p.get("route_type") || "cross",
    brand_id: p.get("brand_id") || "",
    model_id: p.get("model_id") || "",
    buy_region: splitList(p.get("buy_region")),
    sell_region: splitList(p.get("sell_region")),
    min_gap: p.get("min_gap") || "",
    q: p.get("q") || "",
    core_only: p.get("core_only") === "1",
    model_ids: p.get("model_ids") || "",
    signal: p.get("signal") || "",
    sort: p.get("sort") || VIEW_SORT[view] || "opp_score",
    dir: p.get("dir") === "asc" ? "asc" : "desc",
    page: Math.max(1, Number(p.get("page")) || 1),
  };
};

const fetchRoutes = async (storeIdM, params) => {
  const query = { store_id_m: storeIdM, per_page: PER_PAGE, group: "reference" };
  Object.entries(params).forEach(([key, value]) => {
    if (key === "view" || key === "signal" || value === "" || value === false) return;
    if (key === "route_type" && value === "all") return;
    if (MULTI.includes(key)) {
      if (value.length) query[key] = value.join(",");
      return;
    }
    query[key] = value === true ? 1 : value;
  });
  const { data } = await axios.get("/api/core-list/arbitrage/routes", { params: query });
  return data;
};

const RegionMark = ({ region }) => {
  const code = regionFlag(region);
  return code ? (
    <ReactCountryFlag countryCode={code} svg className="arb-flag" aria-hidden="true" />
  ) : (
    <Icon name="Globe" size="sm" className="arb-flag arb-flag--globe" aria-hidden="true" />
  );
};

const dataPoints = (n) => `${n} data point${n === 1 ? "" : "s"}`;

/**
 * Typical gap between an asking price and the price a watch sells for, by pool.
 * Mirrors ArbitrageOpportunityService::ASK_DISCOUNT (defaults until learned from
 * the store's own sales); used for "sells ≈" and for other route types' profit.
 */
const ASK_DISCOUNT = { market: 0.08, trade: 0.03 };
const realistic = (pool, median) => Math.round(median * (1 - (ASK_DISCOUNT[pool] ?? 0)));

const Leg = ({ side, pool, region, median, quotes, currency, showRealistic }) => (
  <div className={`arb-leg is-${side}`}>
    <span className="arb-leg__label">
      {side === "buy" ? "Buy" : "Sell"} · {POOL_LABEL[pool] || pool}
    </span>
    <span className="arb-leg__region">
      <RegionMark region={region} />
      {regionLabel(region)}
    </span>
    <span className="arb-leg__price">
      <Money value={median} currency={currency} decimals={0} />
    </span>
    <span className="arb-leg__depth">
      {showRealistic && (
        <>sells ≈ <Money value={realistic(pool, median)} currency={currency} decimals={0} size="sm" /> · </>
      )}
      {dataPoints(quotes)}
    </span>
  </div>
);

const ROUTE_LABEL = { trade: "Trade → Trade", market: "Market → Market", cross: "Trade ↔ Market" };

/** The popup's exit animation; kept in step with arbitrage-globe.scss. */
const CLOSE_MS = 160;

/** Region prices shown on a card before the rest are counted. */
const MAX_PRICE_CHIPS = 6;

const POOL_SHORT = { trade: "T", market: "M" };

/** The Opportunity score's five equal parts, in the order the bar draws them. */
const PARTS = [
  { key: "profit", label: "Profit" },
  { key: "roi", label: "Return" },
  { key: "demand", label: "Demand" },
  { key: "supply", label: "Supply" },
  { key: "conf", label: "Confidence" },
];
const PART_POINTS = 20;

const confidenceLabel = (c) => (c >= 70 ? "High" : c >= 45 ? "Medium" : "Low");
const sellsIn = (days) => (days < 10 ? `${days} days` : `${Math.round(days / 7)} wks`);

/**
 * One card per REFERENCE (Alex, 2026-10-13). Read top to bottom:
 *
 *   the watch                                  its score (or best number)
 *   one stacked bar of the score's five parts, each part's fact beneath
 *   deal sheet: how fast it sells, stock you hold, last margin
 *   BEST ROUTE   buy here  →  sell there
 *   PRICE BY MARKET   every region's median, cheapest first
 *   also on this watch: the best of each other route type     View map
 */
const ReferenceCard = ({ card, view, currency, rank, total, onOpen, catalogue, prefers }) => {
  const best = card.routes?.[0];
  if (!best) return null;

  const supplyView = view === "supply_pressure";
  const money = view === "opportunity" || view === "profit_week";
  const legKey = (pool, region) => `${pool}|${region}`;
  const buyKey = legKey(best.buy_pool, best.buy_region);
  const sellKey = legKey(best.sell_pool, best.sell_region);

  const prices = [...(card.legs || [])].sort((a, b) => a.median - b.median);
  const hiddenPrices = Math.max(0, prices.length - MAX_PRICE_CHIPS);

  // Other route types for this watch, from its legs — the route-type tab only
  // returns the selected type, and the point of the footer is the other ones.
  const others = {};
  routesFromLegs(card.legs || []).forEach((r) => {
    if (r.type === best.route_type) return;
    const profit = realistic(r.sell.pool, r.sell.median) - r.buy.median;
    if (money && profit <= 0) return;
    if (!others[r.type] || profit > others[r.type].profit) others[r.type] = { ...r, profit };
  });
  const alsoTypes = Object.values(others);

  const parts = best.score_parts;
  const facts = {
    profit: best.profit != null ? <Money value={best.profit} currency={currency} decimals={0} size="sm" /> : "—",
    roi: best.roi != null ? `${best.roi}%` : "—",
    demand: `${card.wtb_leads} WTB${best.wtb_at_price ? `, ${best.wtb_at_price} at price` : ""}`,
    supply: best.buy_share != null ? `${Math.round(best.buy_share * 100)}% buy-side` : "—",
    conf: best.confidence != null ? confidenceLabel(best.confidence) : "—",
  };

  const open = () => onOpen({ ...card, ...best });

  return (
    <li
      className="arb-card"
      role="button"
      tabIndex={0}
      aria-label={`${card.reference}: ${card.route_count} route${card.route_count === 1 ? "" : "s"}, open the arbitrage map`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
    >
      <div className="arb-card__head">
        <div className="arb-card__media">
          {card.image_file_id ? (
            <img src={getFileUrl(card.image_file_id)} alt="" loading="lazy" />
          ) : (
            <span className="arb-card__media-empty" aria-hidden="true" />
          )}
        </div>
        <div className="arb-card__ref">
          <span className="arb-card__reference" title={card.reference}>{card.reference}</span>
          <span className="arb-card__name" title={`${card.brand} · ${card.model}`}>
            {card.brand} · {card.model}
          </span>
          <span className="arb-card__routes">
            {card.route_count} route{card.route_count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="arb-card__gap">
          {view === "opportunity" && best.opp_score != null ? (
            <>
              <span className="arb-card__score">{best.opp_score}<small>/100</small></span>
              <span className="arb-card__gap-caption">#{rank} of {total.toLocaleString()} today</span>
            </>
          ) : view === "profit_week" && best.profit_week != null ? (
            <>
              <span className="arb-card__gap-percent"><Money value={best.profit_week} currency={currency} decimals={0} /></span>
              <span className="arb-card__gap-caption">per week</span>
            </>
          ) : (
            <>
              <span className="arb-card__gap-percent">+{best.gap_percent}%</span>
              <span className="arb-card__gap-caption">
                {supplyView ? `score ${best.score}` : <>gap <Money value={best.gap} currency={currency} decimals={0} /></>}
              </span>
            </>
          )}
        </div>
      </div>

      {card.sequence && <SequenceMatch match={card.sequence} prefers={prefers} catalogue={catalogue} currency={currency} />}

      {view === "opportunity" && parts && (
        <div className="arb-card__score-block">
          <div
            className="arb-stack"
            role="img"
            aria-label={PARTS.map((p) => `${p.label} ${Math.round((parts[p.key] * PART_POINTS) / 100)} of ${PART_POINTS}`).join(", ")}
          >
            {PARTS.map((p) => (
              <i key={p.key} className={`is-${p.key}`} style={{ width: `${(parts[p.key] * PART_POINTS) / 100}%` }} />
            ))}
          </div>
          <div className="arb-stack__key">
            {PARTS.map((p) => (
              <span key={p.key} title={`${p.label}: ${Math.round((parts[p.key] * PART_POINTS) / 100)} of ${PART_POINTS} points`}>
                <i className={`arb-stack__sw is-${p.key}`} aria-hidden="true" />
                <b>{facts[p.key]}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="arb-card__deal">
        {best.sell_days != null && (
          <span className="arb-pill">Sells in ~{sellsIn(best.sell_days)} · {regionLabel(best.sell_region)}</span>
        )}
        <span className={`arb-pill${card.held >= 3 ? " is-warn" : ""}`}>
          {card.held ? `You hold ${card.held}` : "None in stock"}
          {card.last_margin != null ? ` · last margin ${card.last_margin}%` : ""}
        </span>
      </div>

      <div className="arb-card__section">
        <span className="arb-card__label">
          <span className={`arb-card__type-text is-${best.route_type}`}>{ROUTE_LABEL[best.route_type]}</span>
        </span>
        <div className="arb-card__legs">
          <Leg side="buy" pool={best.buy_pool} region={best.buy_region}
            median={best.buy_median} quotes={best.buy_quotes} currency={currency} />
          <Icon name="ArrowRight" size="sm" className="arb-card__arrow" />
          <Leg side="sell" pool={best.sell_pool} region={best.sell_region}
            median={best.sell_median} quotes={best.sell_quotes} currency={currency} showRealistic={money} />
        </div>
      </div>

      <div className="arb-card__section">
        <span className="arb-card__label">Markets, cheapest first</span>
        <ul className="arb-card__prices">
          {prices.slice(0, MAX_PRICE_CHIPS).map((leg) => {
            const key = legKey(leg.pool, leg.region);
            const tone = key === buyKey ? " is-buy" : key === sellKey ? " is-sell" : "";
            return (
              <li
                key={key}
                className={`arb-price${tone}`}
                title={`${POOL_LABEL[leg.pool]} · ${regionLabel(leg.region)} — ${dataPoints(leg.quotes)}`}
              >
                <RegionMark region={leg.region} />
                <span className="arb-price__pool">{POOL_SHORT[leg.pool]}</span>
                <Money value={leg.median} currency={currency} decimals={0} size="sm" />
              </li>
            );
          })}
          {hiddenPrices > 0 && <li className="arb-price arb-price--more">+{hiddenPrices}</li>}
        </ul>
      </div>

      <div className="arb-card__foot">
        <span className="arb-card__also">
          {alsoTypes.length > 0 && (
            <>
              <span className="arb-card__also-lab">Also:</span>
              {alsoTypes.map((r) => (
                <span key={r.type} className="arb-card__also-item">
                  {ROUTE_LABEL[r.type]} ·{" "}
                  <strong>
                    {money ? <><Money value={r.profit} currency={currency} decimals={0} size="sm" /> profit</> : `+${r.gapPercent}%`}
                  </strong>
                </span>
              ))}
            </>
          )}
        </span>
        <span className="arb-card__open">
          View map <Icon name="ArrowRight" size="sm" />
        </span>
      </div>
    </li>
  );
};

const Pager = ({ page, lastPage, onPage }) =>
  lastPage > 1 ? (
    <nav className="arb__pager" aria-label="Pages">
      <Button variant="secondary" size="md" leadingIcon={<Icon name="ChevronLeft" size="sm" />}
        disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span>
        Page {page} of {lastPage}
      </span>
      <Button variant="secondary" size="md" trailingIcon={<Icon name="ChevronRight" size="sm" />}
        disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </nav>
  ) : null;

/** A saved sequence's tab: live/paused line, then its matching watches. */
const SequenceResults = ({ sequence, query, catalogue, onEdit, onOpen, onPage }) => {
  const { data, isFetching, isError, refetch } = query;
  if (isError) {
    return <StatePanel state="error" title="Couldn't load this sequence" description="Please try again." onRetry={() => refetch()} />;
  }
  if (!sequence || (isFetching && !data)) {
    return <StatePanel state="loading" compact title="Loading sequence…" />;
  }

  const cards = data?.data || [];
  const currency = data?.base_currency || "gbp";
  // The prefers the server is using tonight, in weight order, for each card's checklist.
  const ignored = data?.status?.ignored || [];
  const prefers = ((data?.sequence || sequence).filters || []).filter((f) => f.mode === "prefer" && !ignored.includes(f.key));

  return (
    <>
      <SequenceCommand sequence={data?.sequence || sequence} status={data?.status || sequence.status} total={data?.total} catalogue={catalogue} onEdit={onEdit} />
      {data?.status?.state === "paused" ? null : cards.length === 0 ? (
        <StatePanel
          state="empty"
          icon="Filter"
          title="No watches match yet"
          description="Matches update every night after the arbitrage run. Loosen a Must, or turn it into a Prefer."
        />
      ) : (
        <>
          <ul className={`arb__grid ${isFetching ? "is-refreshing" : ""}`}>
            {cards.map((card, index) => (
              <ReferenceCard key={card.product_id} card={card} view="opportunity" currency={currency}
                rank={(data.page - 1) * PER_PAGE + index + 1} total={data.total} onOpen={onOpen} catalogue={catalogue} prefers={prefers} />
            ))}
          </ul>
          <Pager page={data.page} lastPage={data.last_page} onPage={onPage} />
        </>
      )}
    </>
  );
};

const CoreListArbitrages = ({ storeIdM }) => {
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(() => readParams(location.search), [location.search]);
  const queryClient = useQueryClient();

  // Saved sequences: their tabs, the open one's results, and the builder
  // (null = closed, { sequence: null } = new).
  const seqId = params.view.startsWith("seq-") ? Number(params.view.slice(4)) : null;
  const [builder, setBuilder] = useState(null);
  const { data: seqData } = useQuery({
    queryKey: ["arbitrage-sequences", storeIdM],
    queryFn: () => fetchSequences(storeIdM),
    enabled: storeIdM != null,
    staleTime: 60_000,
  });
  const sequences = useMemo(() => (seqData?.enabled ? seqData.sequences : []), [seqData]);
  const catalogue = seqData?.catalogue;
  const activeSeq = sequences.find((s) => s.id === seqId) || null;
  const seqResults = useQuery({
    queryKey: ["arbitrage-sequence-results", storeIdM, seqId, params.page],
    queryFn: () => fetchSequenceResults(storeIdM, seqId, params.page, PER_PAGE),
    enabled: storeIdM != null && seqId != null,
    staleTime: 60_000,
    keepPreviousData: true,
  });

  // The route whose globe is open, and whether it is playing its exit.
  const [openRoute, setOpenRoute] = useState(null);
  const [signalsOpen, setSignalsOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  // Start the world map now, so a card opens onto a globe that is already drawn.
  useEffect(() => {
    loadWorld().catch(() => {});
  }, []);

  const closeGlobe = useCallback(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      setOpenRoute(null);
      return;
    }
    setClosing(true);
    window.setTimeout(() => {
      setOpenRoute(null);
      setClosing(false);
    }, CLOSE_MS);
  }, []);

  // Any filter change goes back to page 1; paging itself keeps the filters.
  const update = useCallback(
    (patch) => {
      const next = { ...params, page: 1, ...patch };
      const qs = new URLSearchParams();
      Object.entries(next).forEach(([key, value]) => {
        if (MULTI.includes(key)) {
          if (value?.length) qs.set(key, value.join(","));
          return;
        }
        if (value === "" || value === false || value == null) return;
        if (key === "page" && value === 1) return;
        qs.set(key, value === true ? "1" : String(value));
      });
      history.replace({ pathname: location.pathname, search: qs.toString() });
    },
    [params, history, location.pathname]
  );

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ["arbitrage-routes", storeIdM, params],
    queryFn: () => fetchRoutes(storeIdM, params),
    enabled: storeIdM != null && seqId == null,
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const currency = data?.base_currency || "gbp";
  const routes = data?.data || [];

  const viewOptions = useMemo(
    () => [
      ...VIEWS,
      ...sequences.map((s) => ({
        value: `seq-${s.id}`,
        label: <SequenceTabLabel sequence={s} />,
        count: s.status?.state === "paused" ? undefined : s.match_count,
      })),
    ],
    [sequences]
  );

  const refreshSequences = () => {
    queryClient.invalidateQueries({ queryKey: ["arbitrage-sequences", storeIdM] });
    queryClient.invalidateQueries({ queryKey: ["arbitrage-sequence-results", storeIdM] });
  };
  const facets = data?.facets || { brands: [], models: [], regions: [] };

  const brandOptions = useMemo(
    () => facets.brands.map((b) => ({ value: String(b.id), label: b.name, hint: String(b.routes) })),
    [facets.brands]
  );
  const modelOptions = useMemo(
    () => facets.models.map((m) => ({ value: String(m.id), label: m.name, hint: String(m.routes) })),
    [facets.models]
  );
  const regionOptions = useMemo(
    () =>
      facets.regions
        .filter((r) => !HIDDEN_REGIONS.includes(r))
        .map((r) => ({ value: r, label: regionLabel(r), icon: <RegionMark region={r} /> })),
    [facets.regions]
  );

  // The box shows every keystroke; the request waits for the debounce.
  const [searchText, setSearchText] = useState(params.q);
  useEffect(() => setSearchText(params.q), [params.q]);

  if (isError && seqId == null) {
    return (
      <StatePanel
        state="error"
        title="Couldn't load arbitrage"
        description="Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <section className="arb">
      {/* The two analyses get their own, larger row — they change what every
          card below means, so they outrank the filters (Alex, 2026-10-12). */}
      <div className="arb__top">
        <div className="arb__tabs">
          <Segmented
            className="arb__views"
            size="lg"
            options={viewOptions}
            value={params.view}
            onChange={(view) => update(VIEW_SORT[view] ? { view, sort: VIEW_SORT[view], dir: "desc" } : { view })}
            label="Arbitrage view"
          />
          {seqData?.enabled && (
            <Button variant="ghost" size="lg" className="arb__new-seq" leadingIcon={<Icon name="Plus" size="sm" />}
              onClick={() => setBuilder({ sequence: null })}>
              New sequence
            </Button>
          )}
        </div>
        <Button variant="secondary" size="lg" leadingIcon={<Icon name="Radar" size="sm" />} onClick={() => setSignalsOpen(true)}>
          Market signals
        </Button>
      </div>

      {seqId != null ? (
        <SequenceResults
          sequence={activeSeq}
          query={seqResults}
          catalogue={catalogue}
          onEdit={() => setBuilder({ sequence: activeSeq })}
          onOpen={setOpenRoute}
          onPage={(page) => update({ page })}
        />
      ) : (
      <>
      <div className="arb__controls">
        <Segmented
          options={ROUTE_TYPES}
          value={params.route_type}
          onChange={(route_type) => update({ route_type })}
          label="Route type"
        />
        <Segmented
          options={WINDOWS}
          value={params.months}
          onChange={(months) => update({ months })}
          label="Data period"
        />
      </div>

      <FiltersBar
        label="Filter arbitrages"
        search={
          <SearchInput
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(q) => q !== params.q && update({ q })}
            onClear={() => update({ q: "" })}
            placeholder="Search reference"
            size="md"
          />
        }
        actions={
          <Toggle
            checked={params.core_only}
            onChange={(core_only) => update({ core_only })}
            label="Core List only"
            size="sm"
          />
        }
      >
        <Select
          label="Brand"
          options={brandOptions}
          value={params.brand_id}
          onChange={(brand_id) => update({ brand_id: brand_id ?? "", model_id: "" })}
          placeholder="All brands"
          searchable
          clearable
        />
        <Select
          label="Model"
          options={modelOptions}
          value={params.model_id}
          onChange={(model_id) => update({ model_id: model_id ?? "" })}
          searchable
          clearable
          disabled={!params.brand_id}
          placeholder={params.brand_id ? "All models" : "Pick a brand first"}
        />
        <Select
          label="Buy in"
          options={regionOptions}
          value={params.buy_region}
          onChange={(buy_region) => update({ buy_region: buy_region || [] })}
          multi
          placeholder="Buy anywhere"
          clearable
        />
        <Select
          label="Sell in"
          options={regionOptions}
          value={params.sell_region}
          onChange={(sell_region) => update({ sell_region: sell_region || [] })}
          multi
          placeholder="Sell anywhere"
          clearable
        />
        <Select
          label="Minimum gap"
          options={MIN_GAPS}
          value={params.min_gap}
          onChange={(min_gap) => update({ min_gap: min_gap ?? "" })}
          placeholder="Any gap"
          clearable
        />
        <SortSelect
          options={SORTS}
          field={params.sort}
          direction={params.dir}
          onChange={(sort, dir) => update({ sort, dir })}
        />
      </FiltersBar>

      {/* One line; the detail is a tap away (Alex, 2026-10-13: fewer words). */}
      <details className="arb__explainer">
        <summary>
          <Icon name="Info" size="sm" />
          <span>{EXPLAIN[params.view].line}</span>
          {data?.computed_at && <span className="arb__updated">Updated {String(data.computed_at).slice(0, 16)}</span>}
        </summary>
        <p>{EXPLAIN[params.view].detail}</p>
      </details>

      {params.model_ids && (
        <div className="arb__signal-filter">
          <span>{params.signal || "Models from Market signals"}</span>
          <button type="button" onClick={() => update({ model_ids: "", signal: "" })} aria-label="Clear the signal filter">
            <Icon name="X" size="sm" />
          </button>
        </div>
      )}

      {isFetching && !data ? (
        <StatePanel state="loading" compact title="Loading arbitrage routes…" />
      ) : !data?.computed_at ? (
        <StatePanel
          state="empty"
          icon="Clock"
          title="Not calculated yet"
          description="Arbitrage routes are calculated overnight. The first run will appear here tomorrow morning."
        />
      ) : routes.length === 0 ? (
        <StatePanel
          state="empty"
          icon="Globe"
          title="No routes match these filters"
          description="Each side needs at least two data points and a gap of 5% or more. Try a longer period, All routes, or fewer filters."
        />
      ) : (
        <>
          <p className="arb__count" aria-live="polite">
            {data.total.toLocaleString()} reference{data.total === 1 ? "" : "s"}
          </p>
          <ul className={`arb__grid ${isFetching ? "is-refreshing" : ""}`}>
            {routes.map((route, index) => (
              <ReferenceCard key={route.product_id} card={route} view={params.view} currency={currency}
                rank={(data.page - 1) * PER_PAGE + index + 1} total={data.total} onOpen={setOpenRoute} />
            ))}
          </ul>
          <Pager page={data.page} lastPage={data.last_page} onPage={(page) => update({ page })} />
        </>
      )}
      </>
      )}

      {seqData?.enabled && (
        <SequenceBuilder
          open={builder != null}
          onClose={() => setBuilder(null)}
          storeIdM={storeIdM}
          catalogue={catalogue}
          sequence={builder?.sequence || null}
          currency={currency}
          onSaved={(saved) => {
            setBuilder(null);
            refreshSequences();
            update({ view: `seq-${saved.id}` });
          }}
          onDeleted={() => {
            setBuilder(null);
            refreshSequences();
            update({ view: "opportunity", sort: VIEW_SORT.opportunity, dir: "desc" });
          }}
        />
      )}

      <MarketSignalsPanel
        open={signalsOpen}
        onClose={() => setSignalsOpen(false)}
        storeIdM={storeIdM}
        onShowModels={(ids, label) => update({ model_ids: ids.join(","), signal: label, brand_id: "", model_id: "" })}
      />

      <ArbitrageGlobeModal
        route={openRoute}
        view={seqId != null ? "opportunity" : params.view}
        currency={currency}
        closing={closing}
        onClose={closeGlobe}
        storeIdM={storeIdM}
        months={seqId != null ? String(activeSeq?.months || 3) : params.months}
      />
    </section>
  );
};

export default CoreListArbitrages;
